// Imports backend tRPC client for the dashboard (PE-1584 / PE-1886).
//
// This file ships at Milestone A's exit gate, AHEAD of any UI and ahead of a
// deployed backend. Milestones D (wizard) and E1 (Import Hub) import from here
// and, until `backends/imports` is live in dev, get the MOCK transport — see
// `isImportsBackendMocked()` below. That is the "mocked tRPC client the
// frontend tracks build against" in the parallelization map's gate condition 2.
//
// THE COMMIT PATH IS WIRED (PE-1907/PE-1908). `createImportJob`,
// `confirmImportMapping`, `validateImportJob`, and `commitImportJob` below are
// the wizard's write path and, unlike the two read wrappers, they do NOT fall
// back to fixtures — see the block comment above them for why.
//
// V1 STATUS of `VITE_APP_IMPORTS_BACKEND_TRPC_URL`: it selects the TRANSPORT —
// fixtures vs the live backend for the read wrappers. It is NOT the access
// gate; that is the `import_contacts` / `view_contacts` permission, re-checked
// by the backend on every call. A build-time env var cannot be targeted per org
// and cannot be turned off without a redeploy, so it decides WHERE the client
// points, never WHO may use the tool. (PE-1584's `imports_tab` PostHog rollout
// flag also gated this and was removed at full rollout.)
//
// AI-assisted mapping is gated OFF for V1 (`AI_MAPPING_REVIEW_ENABLED = false`
// in ImportWizardPage.tsx, commit 282f5ab47) — the wizard never calls
// `proposeMapping`; HelloCSV's built-in header matching does the mapping and
// the AI review surfaces don't render. See `isImportsBackendMocked()` below.
//
// CONVENTIONS — mirrors packages/dashboard/src/api/marketing.ts exactly:
//   - The proxy client is intentionally TYPED AS `any`, not as the backend's
//     `ImportsRouter`. Dashboard's tsconfig uses `moduleResolution: "node"`
//     (legacy), which cannot resolve the subpath `exports` a backend package
//     uses internally; typing the proxy via a router import makes tsc follow
//     into backends/* and surface spurious resolution errors. Call arguments
//     and return values are typed via @charitystack/imports-contracts instead,
//     which the dashboard declares as a real workspace dep.
//   - Cached client at module scope.
//   - Clerk token in BOTH `Authorization` and `x-cs-authorization` (PE-1719:
//     CloudFront OAC SigV4 signing overwrites `Authorization` en route to the
//     Lambda).
//   - `cache: 'no-store'` so tRPC GETs aren't HTTP-cached.
//   - AUTH REJECTIONS ARE NOT tRPC ERRORS (QA F-5): the backend's Clerk
//     middleware rejects BEFORE the tRPC mount, so a 401 body is the Hono
//     envelope `{ ok: false, code: 'HTTP_401', message: 'UNAUTHORIZED' }`.
//     The tRPC client still throws (a TRPCClientError wrapping the
//     unparseable response), but `error.data.code` will NOT be
//     'UNAUTHORIZED' — never branch on tRPC error codes for auth failures.
//     Same seam as marketing/pricing (production-proven); handle it
//     generically, as marketing.ts callers do.

import { createTRPCProxyClient, httpBatchLink } from '@trpc/client';
import {
  measureValidateBodyBytes,
  measureValidateHeaderBytes,
  validateBodyTooLargeMessage,
  validateHeadersTooLargeMessage,
  VALIDATE_MAX_BODY_BYTES,
  VALIDATE_MAX_HEADERS_BYTES,
} from '@charitystack/imports-contracts';
import type {
  ConfirmMappingInput,
  ConfirmMappingOutput,
  ImportJob,
  JobsCancelInput,
  JobsCancelOutput,
  JobsCommitInput,
  JobsCommitOutput,
  JobsCreateInput,
  JobsCreateOutput,
  JobsDownloadResultsInput,
  JobsDownloadResultsOutput,
  JobsGetInput,
  JobsGetOutput,
  JobsListInput,
  JobsListOutput,
  JobsPresignSourceTableInput,
  JobsPresignSourceTableOutput,
  JobsReverseInput,
  JobsReverseOutput,
  MappingProposal,
  ProposeMappingInput,
  ProposeMappingOutput,
  TriageSummary,
  ValidateInput,
  ValidateOutput,
} from '@charitystack/imports-contracts';

import { getAuthToken } from '../utils/auth';
import {
  buildMockProposeMappingOutput,
  getMockImportJob,
  listMockImportJobs,
} from './imports.mock';

const IMPORTS_TRPC_URL = import.meta.env.VITE_APP_IMPORTS_BACKEND_TRPC_URL || '';

function normalizeBaseUrl(url: string): string {
  return url.replace(/\/$/, '');
}

export function isImportsBackendEnabled(): boolean {
  return Boolean(IMPORTS_TRPC_URL);
}

/**
 * True when no imports backend URL is configured, i.e. the wrappers below are
 * serving fixture data from `imports.mock.ts`.
 *
 * Milestones D and E1 build against this: the UI is developed and reviewed
 * with the mock. Surface it in dev builds (a banner/badge) so nobody demos
 * fixture data believing it is real.
 *
 * THIS IS ALSO THE WIZARD'S PRE-FLIGHT CHECK. The commit-path wrappers throw
 * rather than mock, so the wizard tests this BEFORE starting an import and
 * refuses up front with an explanation — far better than letting the user map
 * every column and only then meet a transport error on the final click.
 *
 * ⚠️ SEPARATE GATE, DO NOT CONFLATE: AI-assisted mapping is additionally gated
 * OFF in the wizard by `AI_MAPPING_REVIEW_ENABLED` (V1 ships the vendor's own
 * unassisted mapping step), so `proposeMapping` is not called at all regardless
 * of this URL. Setting the URL enables the commit path; it does not turn AI
 * mapping back on. If you re-enable that flag, note the older hazard it
 * carried: `proposeMapping` NOT_FOUNDs for a job that was never created, so it
 * must run AFTER `createImportJob` for the same jobId — which is the order the
 * wizard now uses.
 *
 * ⚠️ ALSO NOT THE ACCESS GATE. "Mocked vs live" is a transport question. Who
 * may reach the Import Tool at all is the `import_contacts` / `view_contacts`
 * permission, which the backend re-checks on every call. Setting this URL does
 * not expose the tool, and unsetting it does not hide it — it only decides
 * whether what renders is real data or fixtures.
 */
export function isImportsBackendMocked(): boolean {
  return !isImportsBackendEnabled();
}

// Keep this untyped so the dashboard package does not import backend-only
// router modules. See file header for rationale.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let cachedClient: any = null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getImportsClient(): any {
  const url = normalizeBaseUrl(IMPORTS_TRPC_URL);
  if (!url) {
    throw new Error(
      'Imports backend is not configured. Set VITE_APP_IMPORTS_BACKEND_TRPC_URL in the dashboard env (Amplify Console or .env-cmdrc.json).',
    );
  }

  if (!cachedClient) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cachedClient = createTRPCProxyClient<any>({
      links: [
        httpBatchLink({
          url,
          // ONE CALL PER REQUEST (PE-1991). Batching would defeat `validate`'s
          // pre-flight size check: the wrapper measures its OWN payload, then
          // hands the call to this link, which may merge it with any other
          // imports call issued in the same tick into a single POST. A validate
          // sitting just under the budget plus a concurrent `jobs.list` poll
          // (the Import Hub polls on an interval) produces a combined body over
          // the edge ceiling — and the guard, having already passed, cannot see
          // it. The result would be the invisible edge 413 this whole guard
          // exists to prevent, appearing only under a timing coincidence.
          //
          // The cost is real but small: imports calls are user-paced wizard
          // steps and a paginated list, not a chatty read surface, so there is
          // no batching win to lose here.
          maxItems: 1,
          async headers() {
            const token = await getAuthToken();
            return {
              // Both headers, per PE-1719 — see file header.
              ...(token
                ? { Authorization: token, 'x-cs-authorization': token }
                : {}),
            };
          },
          fetch(input, init) {
            return fetch(input, { ...init, cache: 'no-store' });
          },
        }),
      ],
    });
  }

  return cachedClient;
}

// ---------------------------------------------------------------------------
// Wrappers — typed via @charitystack/imports-contracts
// ---------------------------------------------------------------------------

/**
 * SPEC §3.1's GSI query — "all jobs for this org, newest first" (the Import
 * Hub listing, §6.1).
 *
 * Falls back to the mock when no backend URL is configured, so E1 renders a
 * full Hub — all 13 badge states — with no deployed backend.
 */
export async function listImportJobs(
  input: JobsListInput = { limit: 30 },
): Promise<JobsListOutput> {
  if (isImportsBackendMocked()) {
    return listMockImportJobs(input);
  }
  return getImportsClient().jobs.list.query(input);
}

/**
 * SPEC §4.2 / §5.1 — `{ jobId, headers, sampleRows }` → the full
 * `proposeMappingOutputSchema` wrapper (proposal + tier + resolved/unresolved
 * headers + the manual-fallback flags). This is the `imports.proposeMapping`
 * procedure, mounted at the ROOT of the imports router.
 *
 * TWO CONTRACT PROPERTIES THE CALLER MUST HONOUR, both enforced upstream:
 *
 *   - `manualMappingRequired: true` is a SUCCESS response, not an error. It
 *     covers BOTH the degraded path (P2 obligation 5: "AI assists, never
 *     gates") and the duplicate-header path — `manualMappingReason` says
 *     which. Neither arrives as a thrown tRPC error; do not turn either into
 *     a rejected promise.
 *   - `sampleRows` are RAW. Redaction is SERVER-side, on the backend's side of
 *     the port boundary, before anything reaches a provider (INV-5, annex §4.2
 *     obligation 6: "no adapter is ever the component trusted to redact").
 *     Pre-redacting in the browser would move that trust to the client, which
 *     is exactly the inversion the invariant forbids.
 *
 * Falls back to the fixture engine when no backend URL is configured, exactly
 * as `listImportJobs` does. V1 ships with no REACHABLE caller of this wrapper
 * — the wizard's `customSuggestedMapper` (its only call site) is handed to
 * HelloCSV only when the AI gate is on (see `isImportsBackendMocked()` above
 * and `AI_MAPPING_REVIEW_ENABLED`). For whoever
 * re-enables it: the env flip alone is NOT sufficient. Until the wizard wires
 * `createJob` (PE-1907/PE-1908), a live backend NOT_FOUNDs every call here
 * (tenant-scoped job lookup for a job the client never created) and the
 * wizard silently falls back to manual mapping.
 */
export async function proposeMapping(
  input: ProposeMappingInput,
): Promise<ProposeMappingOutput> {
  if (isImportsBackendMocked()) {
    return buildMockProposeMappingOutput(input);
  }
  return getImportsClient().proposeMapping.mutate(input);
}

/**
 * SPEC §6.1's Reverse — undo a completed import (PE-1983 / QA F-8).
 *
 * REQUESTS the reversal; it does not perform it. The procedure fires
 * `imports/job.reversal-requested` and returns the job still in SUCCESS — the
 * workflow claims it into REVERSING moments later. Callers must therefore
 * refresh/poll for the status change rather than trusting the returned job's
 * status, which is a pre-claim snapshot by construction.
 *
 * NOT mocked. `listImportJobs`/`proposeMapping` fall back to fixtures so the
 * UI can be built with no backend, but silently pretending to reverse an
 * import would be a destructive action reported as done while nothing
 * happened. With no backend configured this throws, and the Hub keeps the
 * confirm disabled in mock mode so a user never reaches it.
 */
export async function reverseImportJob(input: JobsReverseInput): Promise<JobsReverseOutput> {
  if (isImportsBackendMocked()) {
    throw new Error('Reverse is unavailable: the imports backend is not configured.');
  }
  return getImportsClient().jobs.reverse.mutate(input);
}

/**
 * `imports.jobs.cancel` — §3.2's two-mechanism cancel. On a pre-commit job
 * (STAGED/MAPPING/VALIDATING/READY) it is a clean transition to CANCELLED
 * ("nothing was written"), which is what the Hub's Discard uses; on a
 * COMMITTING job it sets `cancelRequested` for the workflow to honour.
 *
 * THROWS in mock mode, same reasoning as `reverseImportJob`: silently
 * mock-succeeding a state transition would report a discard as done while the
 * job (if it existed) was untouched.
 */
export async function cancelImportJob(input: JobsCancelInput): Promise<JobsCancelOutput> {
  if (isImportsBackendMocked()) {
    throw new Error('Cancel is unavailable: the imports backend is not configured.');
  }
  return getImportsClient().jobs.cancel.mutate(input);
}

/**
 * SPEC §6.1's results-file download — presigns the per-row outcomes file for a
 * finished job. `kind` selects between the import's own results and the
 * reversal's, because a REVERSED job carries BOTH and §6.1 renders both.
 *
 * A MUTATION, not a query, even though it reads. That is the procedure's shape
 * (`.mutation()` in `download-results.ts`), and it is the right one: presigning
 * mints a time-boxed read capability for contact PII, which is a side effect and
 * must never be replayed by a query cache or a refetch-on-focus. Callers must
 * not reach for a query hook.
 *
 * EXPIRY IS THE CALLER'S JOB FIRST. The Hub computes availability client-side
 * (`resultsAvailability`) and does not render a control for an expired file, so
 * the server's expired-file NOT_FOUND is a backstop for a stale page, not the
 * primary path. Both still have to be handled — see the error note below.
 *
 * NOT mocked, for the same reason as `reverseImportJob`: fabricating a URL would
 * hand back something that cannot download. With no backend configured this
 * throws.
 *
 * ERROR SHAPE — do not branch on tRPC codes alone. `NOT_FOUND` is genuinely a
 * tRPC error here (absent file, expired file, job not in this org). But this
 * procedure is gated on `view_contacts`, and a permission/auth rejection is
 * thrown by the Clerk middleware BEFORE the tRPC mount, so it arrives as the
 * Hono envelope with `error.data.code` UNSET (see the file header, QA F-5).
 * Callers need a generic fallback message, not an exhaustive code switch.
 */
export async function downloadImportResults(
  input: JobsDownloadResultsInput,
): Promise<JobsDownloadResultsOutput> {
  if (isImportsBackendMocked()) {
    throw new Error('Downloads are unavailable: the imports backend is not configured.');
  }
  return getImportsClient().jobs.downloadResults.mutate(input);
}

/**
 * SPEC §6.2's per-job poll — `imports.jobs.get`, the single-record read the
 * contract names for exactly this purpose.
 *
 * WHY THIS EXISTS ALONGSIDE `listImportJobs`. The Hub's poll re-reads the FIRST
 * page, which can never observe a job that has aged onto page two or later —
 * and a reversal is most often started from a job the user paged BACK to. The
 * list poll keeps the row in place but has no fresh data for it, so it would
 * sit at SUCCESS forever, still offering Reverse, while the reversal actually
 * ran. Polling the job by id is the only read that follows it.
 *
 * Mocked like `listImportJobs` (a read, not a destructive action): with no
 * backend configured it serves the fixture of the same id so the Hub stays
 * buildable offline.
 */
export async function getImportJob(input: JobsGetInput): Promise<JobsGetOutput> {
  if (isImportsBackendMocked()) {
    return getMockImportJob(input);
  }
  return getImportsClient().jobs.get.query(input);
}

// ---------------------------------------------------------------------------
// The commit path (PE-1907 / PE-1908)
// ---------------------------------------------------------------------------
//
// ⚠️ THESE FOUR WRAPPERS DELIBERATELY DO NOT FALL BACK TO THE MOCK.
//
// `listImportJobs` and `proposeMapping` are READ-shaped: a fixture answer is a
// harmless stand-in that lets the UI render with no backend. The four below are
// the WRITE path — they create a job, persist a mapping, author `mapped.jsonl`,
// and commit contacts. A mock that resolved successfully would tell the user
// their contacts were imported when nothing was written anywhere, which is the
// exact "mystery state" OVERVIEW bans, and is strictly worse than an error.
//
// So with no backend URL configured these THROW, and the wizard renders the
// failure. `isImportsBackendMocked()` is the check a caller should make BEFORE
// starting a commit it cannot finish — see `ImportWizardPage`'s `onComplete`.

/**
 * SPEC §4.1 / annex §4.3 — creates the job in STAGED **and** returns the
 * presigned S3 PUT for the source file. One procedure, not two, by contract.
 *
 * IDEMPOTENT BY CONTRACT, on the client-generated `jobId` (§3.1: the jobId "is
 * the idempotency key — creating a job with the same ID is a NO-OP"). Note
 * *no-op*, not *error*: a retry returns the existing job with
 * `alreadyExisted: true`, so a caller retrying after a transient network
 * failure is safe and must not treat the second call as a conflict.
 */
export async function createImportJob(
  input: JobsCreateInput,
): Promise<JobsCreateOutput> {
  return getImportsClient().createJob.mutate(input);
}

/**
 * Annex §4.3 step 2 — persists O3 (`MappingConfig`) on the job record.
 *
 * TOTALITY IS ENFORCED SERVER-SIDE: `expectedHeaders` is the file's verbatim
 * header list and the procedure refuses a `columns` array that does not cover
 * it exactly, one entry per header, in position order. A skipped column is the
 * explicit `SKIP` sentinel — annex §5: "Absence is NEVER a way to express a
 * decision." Build the array from the headers, never from the mapped subset.
 */
export async function confirmImportMapping(
  input: ConfirmMappingInput,
): Promise<ConfirmMappingOutput> {
  return getImportsClient().confirmMapping.mutate(input);
}

/**
 * Annex §4.3 step 3 — authors `mapped.jsonl` (O2) + the `TriageSummary` (O4),
 * and moves the job VALIDATING → READY. This is also the step that writes the
 * top-level `mappingConfigHash` the commit path asserts against (PE-1975).
 *
 * ROWS GO RAW AND COMPLETE. Every data row in file order — not a preview —
 * with each row's width equal to `headers.length`. Transforms, validation, and
 * consent resolution all run server-side over these as an untrusted
 * `ParsedTable` (INV-5): normalizing or redacting in the browser would move
 * that trust to the client, the inversion the invariant forbids.
 *
 * A FAILED RECONCILIATION IS NOT AN ERROR. `reconciles: false` comes back on a
 * SUCCESS response with `reconciliationDetail` — the annex requires the wizard
 * to RENDER that rather than the user meeting a 500. Do not throw on it.
 *
 * THE PRE-FLIGHT SIZE CHECK IS LOAD-BEARING (PE-1991). Because every data row
 * rides in the POST body, the shared `ApiRouter`'s Lambda@Edge signer rejects a
 * >~1MB body BEFORE it reaches our origin. That 413 is generated at the edge,
 * so the backend never runs: no Powertools log, no CloudWatch entry, no alarm,
 * and a body that is neither our Hono envelope nor a tRPC one. There is no
 * server-side fix — by the time our code could respond, CloudFront has already
 * answered.
 *
 * So the ONLY place a large file can be refused with an explanation is here,
 * before the request leaves the browser. The `validate` procedure carries the
 * same check for callers that bypass this wrapper, but in the deployed path this
 * client-side check is the one that actually fires.
 *
 * Measured ceiling on deployed dev: 12,000 rows x 2 cols (0.49 MB) succeeds
 * while 15,000 rows x 4 cols (1.17 MB) 413s — the unit is BYTES, not rows,
 * which is why the budget is measured off the serialized payload rather than
 * `rows.length`.
 */
export async function validateImportJob(
  input: ValidateInput,
): Promise<ValidateOutput> {
  // The header row is user-controlled on BOTH transports and can approach 1 MB
  // on its own (500 entries, each unbounded in length), so it is checked either
  // way. On the S3 path it is the only user-controlled field left in the body.
  const headerBytes = measureValidateHeaderBytes(input.headers);
  if (headerBytes > VALIDATE_MAX_HEADERS_BYTES) {
    throw new ImportHeadersTooLargeError(headerBytes, input.headers.length);
  }

  // The row-bytes budget applies only when the rows are IN the body. On the S3
  // transport they are not, which is the entire point of ADM-35 — the 1 MiB
  // edge cap stops bounding how large a file can be imported.
  if (input.source !== 's3') {
    const bodyBytes = measureValidateBodyBytes(input.rows, input.headers);
    if (bodyBytes > VALIDATE_MAX_BODY_BYTES) {
      throw new ImportFileTooLargeError(bodyBytes, input.rows.length);
    }
  }

  return getImportsClient().validate.mutate(input);
}

/**
 * Annex §4.3 / §4.4 — records the attestation and fires
 * `imports/job.committed`. After this the Inngest workflow owns the job; the
 * Hub polls for progress.
 *
 * `attested: false` IS A VALID OUTCOME, not a validation failure. SPEC §9.1:
 * declining still imports — the contacts are stored and visible, just excluded
 * from marketing sends. §4.4's "(required checkbox)" is UI copy for the
 * interaction, not a server-side validity gate. Never block the call on it.
 */
export async function commitImportJob(
  input: JobsCommitInput,
): Promise<JobsCommitOutput> {
  return getImportsClient().commit.mutate(input);
}

/**
 * PUT the source file at the presigned URL returned by `createImportJob`.
 *
 * Deliberately a bare `fetch`, NOT the tRPC client: the URL is a presigned S3
 * endpoint, and sending our Clerk `Authorization` header to it would conflict
 * with the SigV4 query-string signature S3 validates. Send the bytes and
 * nothing else.
 *
 * No `Content-Type` either — the presign is generated without one, and S3
 * includes `Content-Type` in the signed canonical request when present, so
 * adding it here produces `SignatureDoesNotMatch` on a URL that is otherwise
 * valid.
 */
export async function uploadImportSourceFile(
  uploadUrl: string,
  file: Blob,
): Promise<void> {
  const response = await fetch(uploadUrl, { method: 'PUT', body: file });
  if (!response.ok) {
    throw new Error(
      `Uploading the file failed (${response.status} ${response.statusText}).`,
    );
  }
}

/**
 * ADM-35 — mint a FRESH, write-once key for the derived table and get a
 * presigned PUT for it.
 *
 * CALLED IMMEDIATELY BEFORE EACH UPLOAD, including before a re-validate. That
 * is the entire reason the procedure exists: a presigned URL is a capability
 * against exactly ONE server-derived key, and `createJob` runs only once per
 * job (on replay it re-presigns the STORED key), so there was no way to obtain
 * a second key mid-job. Reusing one key would let a re-validate overwrite an
 * object a still-running validate is reading, which produces a job pointing at
 * a dataset built from a table the user has since changed — valid hash, wrong
 * donor data, no alarm.
 *
 * The nonce is minted SERVER-SIDE; nothing here supplies key material.
 */
export async function presignImportSourceTable(
  input: JobsPresignSourceTableInput,
): Promise<JobsPresignSourceTableOutput> {
  return getImportsClient().jobs.presignSourceTable.mutate(input);
}

/**
 * PUT the serialized derived table (O6 NDJSON) at its presigned URL.
 *
 * SAME TWO HEADER CONSTRAINTS AS `uploadImportSourceFile`, and they are not
 * stylistic. No `Authorization`: the URL carries a SigV4 query-string
 * signature, and sending our Clerk header alongside it makes S3 reject the
 * request. No `Content-Type`: the presign is generated without one, and S3
 * includes `Content-Type` in the signed canonical request WHEN PRESENT, so
 * adding it here produces `SignatureDoesNotMatch` on a URL that is otherwise
 * valid. Send the bytes and nothing else.
 */
export async function uploadImportSourceTable(
  uploadUrl: string,
  ndjson: string,
): Promise<void> {
  const response = await fetch(uploadUrl, { method: 'PUT', body: ndjson });
  if (!response.ok) {
    throw new Error(
      `Uploading the prepared table failed (${response.status} ${response.statusText}).`,
    );
  }
}

/**
 * Thrown by `validateImportJob()` when the row payload exceeds the transport
 * budget, BEFORE any request is sent.
 *
 * A distinct class rather than a bare `Error` so the wizard can render this as
 * a normal "your file is too big, here's what to do" step — the same weight as
 * a validation warning — instead of the generic "something went wrong" surface
 * a network failure gets. `message` is already user-facing prose (it comes from
 * the shared contract), so a caller may render it verbatim.
 */
export class ImportFileTooLargeError extends Error {
  readonly bodyBytes: number;
  readonly limitBytes: number;
  readonly rowCount: number;

  constructor(bodyBytes: number, rowCount: number) {
    super(validateBodyTooLargeMessage(bodyBytes, rowCount));
    this.name = 'ImportFileTooLargeError';
    this.bodyBytes = bodyBytes;
    this.limitBytes = VALIDATE_MAX_BODY_BYTES;
    this.rowCount = rowCount;
  }
}

/**
 * Thrown when the HEADER ROW alone exceeds the metadata budget (ADM-35).
 *
 * A SEPARATE CLASS FROM `ImportFileTooLargeError` because the remedy is
 * different, and offering the wrong one wastes the user's time. "Split the file
 * into smaller batches" does nothing about a header row that is too large —
 * they would do the work and meet the identical refusal. The real cause is
 * almost always a malformed first row (merged cells, or a title pasted above
 * the data), so the message says that instead.
 *
 * Reachable on both transports: `headers` is capped at 500 ENTRIES but each
 * entry's length is unbounded, so it can approach 1 MB on its own.
 */
export class ImportHeadersTooLargeError extends Error {
  readonly headerBytes: number;
  readonly limitBytes: number;
  readonly columnCount: number;

  constructor(headerBytes: number, columnCount: number) {
    super(validateHeadersTooLargeMessage(headerBytes, columnCount));
    this.name = 'ImportHeadersTooLargeError';
    this.headerBytes = headerBytes;
    this.limitBytes = VALIDATE_MAX_HEADERS_BYTES;
    this.columnCount = columnCount;
  }
}

/**
 * Thrown between validate and commit when O4 reports rows a human must resolve
 * (`triageBlocksCommit`, i.e. `needsAttention > 0`).
 *
 * WHY THIS EXISTS AS A THROW RATHER THAN A DISABLED BUTTON. The contract has
 * always said "`needsAttention > 0` BLOCKS COMMIT until resolved or explicitly
 * skipped" (`contracts/imports` dataset.ts, O4), but the wizard validates and
 * commits inside ONE call with no user decision point between them — so the
 * flag had no reader and every flagged row committed silently. That is how a
 * p7 Mailchimp import wrote no consent at all while reporting success: every
 * consent row was flagged `CONSENT_VALUE_UNRECOGNIZED` and nothing surfaced.
 *
 * A distinct class, like `ImportFileTooLargeError`, so the wizard renders this
 * as an ordinary "here's what needs your attention" step rather than the
 * generic failure surface. The counts travel on the error because the caller
 * needs them to render the explanation.
 */
export class ImportNeedsAttentionError extends Error {
  readonly needsAttention: number;
  readonly triage: TriageSummary;

  constructor(triage: TriageSummary) {
    super(
      `${triage.needsAttention} of ${triage.total} rows need review before this import can ` +
        'run. Open the flagged rows, resolve them, and commit again.',
    );
    this.name = 'ImportNeedsAttentionError';
    this.needsAttention = triage.needsAttention;
    this.triage = triage;
  }
}

export type {
  ConfirmMappingInput,
  ConfirmMappingOutput,
  ImportJob,
  JobsCommitInput,
  JobsCommitOutput,
  JobsCancelInput,
  JobsCancelOutput,
  JobsCreateInput,
  JobsCreateOutput,
  JobsDownloadResultsInput,
  JobsDownloadResultsOutput,
  JobsGetInput,
  JobsGetOutput,
  JobsListInput,
  JobsListOutput,
  JobsPresignSourceTableInput,
  JobsPresignSourceTableOutput,
  JobsReverseInput,
  JobsReverseOutput,
  MappingProposal,
  ProposeMappingInput,
  ProposeMappingOutput,
  ValidateInput,
  ValidateOutput,
};
